import type { RenewalFamily } from "@/types/renewals";

import { downloadBlob } from "@/components/feature/pdf/helpers";
import { type CsvCell, downloadCsv, toCsv } from "@/helpers/csv";

import { ACTION_DATE_LABEL, LEGAL_REVIEW_LABELS, SPEND_CATEGORY_LABELS, statusLabel } from "./constants";

interface Column {
  header: string;
  value: (family: RenewalFamily, ownerLabel: string) => CsvCell;
  // Decimal strings from the API that a spreadsheet should hold as numbers.
  amount?: boolean;
}

const COLUMNS: Column[] = [
  { header: "Supplier", value: family => family.supplier_name },
  { header: "Contract Family", value: family => family.family_name },
  { header: "Contract Title", value: family => family.contract_title },
  {
    header: "Status",
    value: family => (family.procurement_status ? statusLabel(family.procurement_status) : null),
  },
  { header: "L2 Category", value: family => family.category_l2 },
  { header: "L3 Category", value: family => family.category_l3 },
  { header: "Owner", value: (_family, ownerLabel) => ownerLabel },
  { header: "Term End Date", value: family => family.term_end_date },
  { header: ACTION_DATE_LABEL, value: family => family.action_date },
  { header: "Days to Notice", value: family => family.days_to_action },
  {
    header: "Termination Type",
    value: family => family.termination_type,
  },
  {
    header: "Spend Category",
    value: family => (family.spend_category ? SPEND_CATEGORY_LABELS[family.spend_category] : null),
  },
  { header: "Total Contract Value (GBP)", value: family => family.total_contract_value_gbp, amount: true },
  { header: "Contract Value", value: family => family.total_contract_value, amount: true },
  { header: "Currency", value: family => family.currency_code },
  {
    header: "Legal Review",
    value: family => (family.legal_review_status ? LEGAL_REVIEW_LABELS[family.legal_review_status] : null),
  },
];

type OwnerLabel = (ownerUserId: string | null) => string;

const rowsOf = (families: RenewalFamily[], ownerLabel: OwnerLabel) =>
  families.map(family => COLUMNS.map(column => column.value(family, ownerLabel(family.owner_user_id))));

export const renewalsCsv = (families: RenewalFamily[], ownerLabel: OwnerLabel) =>
  toCsv([COLUMNS.map(column => column.header), ...rowsOf(families, ownerLabel)]);

const sheetCell = (value: CsvCell, column: Column) => {
  if (column.amount && typeof value === "string" && value !== "") {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : value;
  }
  return value;
};

// SheetJS stores strings as text cells (never formulas), so no escaping is needed here.
export const renewalsWorkbook = async (families: RenewalFamily[], ownerLabel: OwnerLabel) => {
  const XLSX = await import("xlsx");
  const rows = rowsOf(families, ownerLabel).map(row => row.map((value, index) => sheetCell(value, COLUMNS[index])));
  const sheet = XLSX.utils.aoa_to_sheet([COLUMNS.map(column => column.header), ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Contract renewals");
  return XLSX.write(workbook, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
};

export const downloadRenewalsCsv = (csv: string, today: string) => downloadCsv(csv, `contract-renewals-${today}.csv`);

export const downloadRenewalsWorkbook = (bytes: ArrayBuffer, today: string) =>
  downloadBlob(
    new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `contract-renewals-${today}.xlsx`,
  );
